import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { track, setAnalyticsUser } from '@/lib/track';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSessionId = async () => {
      try {
        // Dismiss any existing toasts first
        toast.dismiss();
        
        // Extract session_id from URL - check both hash and query params
        let sessionId = null;
        
        // Try hash fragment first (e.g., #session_id=xxx)
        const hash = window.location.hash;
        if (hash) {
          const hashParams = new URLSearchParams(hash.substring(1));
          sessionId = hashParams.get('session_id');
        }
        
        // If not in hash, try query parameters (e.g., ?session_id=xxx)
        if (!sessionId) {
          const searchParams = new URLSearchParams(window.location.search);
          sessionId = searchParams.get('session_id');
        }

        if (!sessionId) {
          console.error('No session_id found in URL');
          track('login_failed', { method: 'session_exchange', reason: 'no_session_id' });
          toast.error('Invalid authentication response');
          setTimeout(() => navigate('/login'), 1000);
          return;
        }

        // Exchange session_id for session_token
        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );

        if (response.data.success) {
          const user = response.data.user;

          // This is a second, complete sign-in path alongside the Google
          // button on /login — it must emit the same GA4 pair, or every login
          // that lands here is invisible.
          setAnalyticsUser(user);
          track(user.profile_completed ? 'login' : 'sign_up', {
            method: 'session_exchange',
          });

          // Dismiss any error toasts before success
          toast.dismiss();

          // Check if user originally tried to access a product before logging in
          const hasPendingProduct = !!sessionStorage.getItem('pendingProduct');
          
          // Check if profile needs to be completed (new user)
          if (!user.profile_completed) {
            toast.success('Welcome! Please complete your profile');
            // Use setTimeout to ensure toast shows before navigation
            setTimeout(() => {
              navigate('/onboarding', { state: { user }, replace: true });
            }, 500);
          } else if (hasPendingProduct) {
            // Skip the toast and head straight to the surprise interstitial
            setTimeout(() => {
              navigate('/welcome', { state: { user }, replace: true });
            }, 300);
          } else {
            toast.success('Welcome back!');
            // Use setTimeout to ensure toast shows before navigation
            setTimeout(() => {
              navigate('/', { state: { user }, replace: true });
            }, 500);
          }
        } else {
          console.error('Session exchange failed:', response.data);
          track('login_failed', { method: 'session_exchange', reason: 'exchange_rejected' });
          toast.error('Authentication failed');
          setTimeout(() => navigate('/login'), 1000);
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        console.error('Error details:', error.response?.data);
        track('login_failed', {
          method: 'session_exchange',
          reason: 'request_error',
          error_status: error?.response?.status,
        });
        toast.error('Authentication failed. Please try again.');
        setTimeout(() => navigate('/login'), 1000);
      }
    };

    processSessionId();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="auth-callback-loading">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D302A]"></div>
        <p className="mt-4 text-[#6B6A68]">Completing sign in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;